import type { Decision } from "@shared/espace.types";
import type { Tone } from "@/components/ds/Badge";

/**
 * Le vocabulaire de l'espace, et les tons qui vont avec.
 *
 * Deux principes tenus ici :
 *
 * 1. **L'entreprise lit l'effet, pas le statut interne.** « Publiée » lui dit que
 *    des étudiants la voient ; `validee` ne lui dit rien.
 * 2. **La réponse à un profil n'est pas une note.** « Je veux le rencontrer » est
 *    une intention, « Pas pour ce poste » ferme une porte sans juger la personne.
 *    Le vocabulaire vient du parcours de Garry (`oui` / `peut-etre` / `pas`).
 */

export const REQUEST_STATUS: Record<string, { label: string; tone: Tone; help: string }> = {
	nouveau: {
		label: "Reçue",
		tone: "neutral",
		help: "L'école la relit. Vous n'avez rien à faire.",
	},
	a_completer: {
		label: "Complément demandé",
		tone: "warning",
		help: "L'école a besoin d'une précision pour la publier.",
	},
	validee: {
		label: "Publiée",
		tone: "success",
		help: "Les étudiants la voient et peuvent postuler.",
	},
	refusee: {
		label: "Non retenue",
		tone: "neutral",
		help: "L'école n'a pas donné suite à cette demande.",
	},
	// `archivee` n'est PAS un refus, et c'est tout l'intérêt de les distinguer :
	// l'un est la fin d'une durée, l'autre une décision. Les confondre ferait lire
	// un refus là où l'entreprise a simplement laissé passer sa date.
	archivee: {
		label: "Terminée",
		tone: "neutral",
		help: "La date limite est passée. Vous pouvez la redéposer.",
	},
};

export const DECISION_LABEL: Record<Decision, { label: string; short: string; tone: Tone }> = {
	oui: { label: "Je veux le rencontrer", short: "À rencontrer", tone: "success" },
	"peut-etre": { label: "À étudier", short: "À étudier", tone: "warning" },
	pas: { label: "Pas pour ce poste", short: "Écarté", tone: "neutral" },
};

export function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString("fr-FR", {
		day: "numeric",
		month: "long",
		year: "numeric",
	});
}

/** « il y a 3 jours » : dans une liste, l'écart compte plus que la date. */
export function relativeDate(iso: string): string {
	const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
	if (days <= 0) return "aujourd'hui";
	if (days === 1) return "hier";
	if (days < 31) return `il y a ${days} jours`;
	const months = Math.round(days / 30);
	return months <= 1 ? "il y a un mois" : `il y a ${months} mois`;
}
